import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import type { ReactNode } from 'react';
import {
    applyHighlightMarks,
    getSelectionAsHighlight,
    getSelectedText,
    getHighlightIdFromElement,
    getHighlightTextById,
    mergeOverlappingHighlights,
} from './highlightUtils';
import type { SerializedHighlight } from '@/src/lib';

export type HighlightContextMenuData = {
    selectedText: string;
    highlightId: string | null;
    hasSelection: boolean;
};

export type HighlightableWrapperHandle = {
    addHighlight: () => void;
    removeHighlight: (id: string) => void;
    getSelectedText: () => string;
};

export type ActiveTool = 'none' | 'highlight' | 'erase';

type HighlightableWrapperProps = {
    highlights: SerializedHighlight[];
    onHighlightsChange: (highlights: SerializedHighlight[]) => void;
    onContextMenu: (e: React.MouseEvent, data: HighlightContextMenuData) => void;
    activeTool: ActiveTool;
    children: ReactNode;
};

export const HighlightableWrapper = forwardRef<
    HighlightableWrapperHandle,
    HighlightableWrapperProps
>(function HighlightableWrapper(
    { highlights, onHighlightsChange, onContextMenu, activeTool, children },
    ref
) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const highlightsRef = useRef(highlights);
    highlightsRef.current = highlights;

    useEffect(() => {
        if (wrapperRef.current) {
            applyHighlightMarks(wrapperRef.current, highlights);
        }
    }, [highlights]);

    const addHighlight = useCallback(() => {
        if (!wrapperRef.current) {
            return;
        }
        const hl = getSelectionAsHighlight(wrapperRef.current);
        if (!hl) {
            return;
        }
        const next = mergeOverlappingHighlights(highlightsRef.current, hl);
        onHighlightsChange(next);
        window.getSelection()?.removeAllRanges();
    }, [onHighlightsChange]);

    const removeHighlight = useCallback(
        (id: string) => {
            onHighlightsChange(highlightsRef.current.filter((h) => h.id !== id));
        },
        [onHighlightsChange]
    );

    const getSelectedTextInWrapper = useCallback(() => {
        if (!wrapperRef.current) {
            return '';
        }
        return getSelectedText(wrapperRef.current);
    }, []);

    useImperativeHandle(ref, () => ({
        addHighlight,
        removeHighlight,
        getSelectedText: getSelectedTextInWrapper,
    }));

    useEffect(() => {
        if (activeTool !== 'highlight') {
            return;
        }

        const handleMouseUp = () => {
            const selection = window.getSelection();
            if (
                !selection ||
                selection.isCollapsed ||
                !wrapperRef.current?.contains(selection.anchorNode)
            ) {
                return;
            }

            requestAnimationFrame(() => {
                addHighlight();
            });
        };

        const wrapper = wrapperRef.current;
        wrapper?.addEventListener('mouseup', handleMouseUp);
        return () => {
            wrapper?.removeEventListener('mouseup', handleMouseUp);
        };
    }, [activeTool, addHighlight]);

    useEffect(() => {
        if (activeTool !== 'erase') {
            return;
        }

        const handleClick = (e: MouseEvent) => {
            const highlightId = getHighlightIdFromElement(e.target as HTMLElement);
            if (highlightId) {
                e.preventDefault();
                removeHighlight(highlightId);
            }
        };

        const handleMouseUp = () => {
            if (!wrapperRef.current) {
                return;
            }
            const hl = getSelectionAsHighlight(wrapperRef.current);
            if (!hl) {
                return;
            }

            requestAnimationFrame(() => {
                const remaining = highlightsRef.current.filter(
                    (h) => h.end <= hl.start || h.start >= hl.end
                );
                if (remaining.length !== highlightsRef.current.length) {
                    onHighlightsChange(remaining);
                }
                window.getSelection()?.removeAllRanges();
            });
        };

        const wrapper = wrapperRef.current;
        wrapper?.addEventListener('click', handleClick);
        wrapper?.addEventListener('mouseup', handleMouseUp);
        return () => {
            wrapper?.removeEventListener('click', handleClick);
            wrapper?.removeEventListener('mouseup', handleMouseUp);
        };
    }, [activeTool, removeHighlight, onHighlightsChange]);

    const handleContextMenu = useCallback(
        (e: React.MouseEvent) => {
            const wrapper = wrapperRef.current;
            if (!wrapper) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            const selection = window.getSelection();
            const hasSelection = !!(
                selection &&
                !selection.isCollapsed &&
                wrapper.contains(selection.anchorNode)
            );
            const selectedText = hasSelection ? (selection?.toString().trim() ?? '') : '';

            const highlightId = getHighlightIdFromElement(e.target as HTMLElement);

            const displayText =
                selectedText ||
                (highlightId ? getHighlightTextById(wrapper, highlightId) : '') ||
                (e.target as HTMLElement)
                    .closest('p, div, span, li, td, th')
                    ?.textContent?.trim() ||
                '';

            onContextMenu(e, {
                selectedText: displayText,
                highlightId,
                hasSelection,
            });
        },
        [onContextMenu]
    );

    const cursorClass =
        activeTool === 'highlight'
            ? 'cursor-highlight'
            : activeTool === 'erase'
              ? 'cursor-erase'
              : '';

    return (
        <div
            ref={wrapperRef}
            onContextMenu={handleContextMenu}
            className={`highlightable-wrapper ${cursorClass}`}
        >
            {children}
        </div>
    );
});
